import React, { useState, useContext } from 'react'
import type { Marker as MarkerType, Timespan as TimespanType, Photo } from '@app-types/entity_types.js'
import { map, sortBy } from 'lodash-es'
import { ThemeContext } from '@base/ThemeContext.js'
import { convertViewThemeToCSSProperties } from '@utils/theme'
import GoogleAd from '@components/ads/GoogleAd'
import GenericAd from '@components/ads/GenericAd'
import TimetoastTemporal from '@base/timetoast_temporal.js'
import Marker from './Marker'
import Timespan from './Timespan'

type ComponentProps = {
  markers: MarkerType[]
  timespans: TimespanType[]
  photos: Record<string, Photo>
  showAds: boolean
  environment: 'development' | 'production'
  adChannel: number | string // could be number, or string with multiple numbers separated by + sign
}

type TimelineItem = MarkerType | TimespanType

const ListTimeline: React.FC<ComponentProps> = props => {
  const { markers = [], timespans = [], photos, showAds = false, environment, adChannel } = props
  const adMixSeed = useState<number>(Math.random())

  const theme = useContext(ThemeContext)
  const style = theme ? convertViewThemeToCSSProperties(theme) : {}

  const getDisplayAdContent = (key: number) => {
    return (
      <li key={key} className="timeline-item timeline-item--viewer reclame reclame--in-content">
        <GoogleAd
          client="ca-pub-3637961829875093"
          slot="3833790973"
          environment={environment}
          showAds={showAds}
          adFormat="auto"
          adChannel={adChannel}
        />
      </li>
    )
  }

  const getNativeInfeedAdContent = (key: number) => {
    return (
      <li key={key} className="timeline-item timeline-item--viewer reclame reclame--in-content">
        <GoogleAd
          client="ca-pub-3637961829875093"
          slot="5307148571"
          environment={environment}
          showAds={showAds}
          adFormat="fluid"
          adLayout="image-top"
          adLayoutKey="-8j+1w-dx+ec+gk"
          adChannel={adChannel}
        />
      </li>
    )
  }

  const getOtherAdNetworkAdSrc = () => {
    return 'some-other-ad-network-string' //window.Timetoast.someOtherAdSrc
  }

  const getOtherAdContent = (key: number) => {
    return (
      <li key={key} className="timeline-item timeline-item--viewer reclame reclame--in-content">
        <GenericAd src={getOtherAdNetworkAdSrc()} environment={environment} showAds={showAds} />
      </li>
    )
  }

  const getTimelineItemsContent = () => {
    const sorterFn = (item: TimelineItem) => {
      // Markers have 'date', timespans have 'start_date'
      if ('start_date' in item && item.start_date) {
        return TimetoastTemporal.parse(item.start_date).getTime()
      } else if ('date' in item && item.date) {
        return TimetoastTemporal.parse(item.date).getTime()
      }
      return 0
    }
    const allItems: (MarkerType | TimespanType)[] = [...markers, ...timespans]
    const sortedItems = sortBy(allItems, [sorterFn])

    let itemCounter = 0
    let items: React.ReactNode[] = map(sortedItems, item => {
      const lazyLoadImage = itemCounter >= 2
      itemCounter++
      if (item.hasOwnProperty('start_date')) {
        // timespan
        return <Timespan key={`list-timespan-item-${item.id}`} data={item as TimespanType} />
      } else {
        // marker
        let photoData = null
        if ('photo_ids' in item && item.photo_ids.length > 0) {
          const photoIdString = item.photo_ids[0].toString()
          photoData = photos[photoIdString]
        }
        return (
          <Marker
            key={`list-marker-item-${item.id}`}
            data={item as MarkerType}
            photoData={photoData}
            lazyLoadImage={lazyLoadImage}
          />
        )
      }
    })
    items = injectAds(items)
    return <ul className="timeline-items">{items}</ul>
  }

  const getAdNetwork = () => {
    return window.Timetoast.adNetwork
  }

  const getAdContent = (key: number) => {
    const adNetwork = getAdNetwork()
    if (adNetwork === 'some-other-ad-network') {
      return getOtherAdContent(key)
    } else {
      // default to adsense
      return adMixSeed[0] < 0.2 ? getNativeInfeedAdContent(key) : getDisplayAdContent(key)
    }
  }

  const injectAds = (items: React.ReactNode[]) => {
    const adPositions = [2, 7, 10, 15, 18, 23] // note 0 indexed so injects ads after item (e.g. 3 will show after timeline item 3)
    if (showAds && items.length >= adPositions[0]) {
      const numItems = items.length
      const ads = [getAdContent(1)]
      if (numItems >= adPositions[1]) {
        ads.push(getAdContent(2))
      }
      if (numItems >= adPositions[2]) {
        ads.push(getAdContent(3))
      }
      if (numItems >= adPositions[3]) {
        ads.push(getAdContent(4))
      }
      if (numItems >= adPositions[4]) {
        ads.push(getAdContent(5))
      }
      if (numItems >= adPositions[5]) {
        ads.push(getAdContent(6))
      }
      if (numItems >= adPositions[6]) {
        ads.push(getAdContent(7))
      }
      if (numItems >= adPositions[7]) {
        ads.push(getAdContent(8))
      }

      // now inject ad into timeline items
      const numAds = ads.length
      for (let i = 0; i < numAds; i++) {
        items.splice(adPositions[i], 0, ads[i])
      }
    }
    return items
  }

  const getNoItemsContent = () => {
    return (
      <div className="p">
        <p>This timeline doesn&apos;t have any content.</p>
      </div>
    )
  }

  const numItems = markers.length + timespans.length
  const content = numItems > 0 ? getTimelineItemsContent() : getNoItemsContent()
  return (
    <div className="timeline timeline--vertical" style={style}>
      {content}
    </div>
  )
}

export default React.memo(ListTimeline)
